import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { authSlice, AuthSlice } from './slices/authSlice';
import { appSlice, AppSlice } from './slices/appSlice';
import { patientsSlice, PatientsSlice } from './slices/patientsSlice';
import { documentsSlice, DocumentsSlice } from './slices/documentsSlice';
import { tasksSlice, TasksSlice } from './slices/tasksSlice';
import { intakeSlice, IntakeSlice } from './slices/intakeSlice';
import { toastSlice, ToastSlice } from './slices/toastSlice';
import { uploadsSlice, UploadsSlice } from './slices/uploadsSlice';
import { payorSlice, PayorSlice } from './slices/payorSlice';
import { rogerDocSlice, RogerDocSlice } from './slices/rogerDocSlice';
import {
  agencyInsuranceSlice,
  AgencyInsuranceSlice,
} from './slices/agencyInsuranceSlice';
import {
  agencyClinicSlice,
  AgencyClinicSlice,
} from './slices/agencyClinicSlice';
import { noaSlice, NOASlice } from './slices/noaSlice';
import {
  oasisManagerSlice,
  OASISManagerSlice,
} from './slices/oasisManagerSlice';
import { payrollSlice, PayrollSlice } from './slices/payrollSlice';
import {
  formSchemaLayoutGenerationSlice,
  FormSchemaLayoutGenerationSlice,
} from './slices/formSchemaLayoutGenerationSlice';
import {
  formSchemaEditorSlice,
  FormSchemaEditorSlice,
} from './slices/formSchemaEditorSlice';
import {
  vibecoderSettingsSlice,
  VibecoderSettingsSlice,
} from './slices/vibecoderSettingsSlice';
import {
  billingUiSlice,
  BillingUiSlice,
  migrateBillingUiState,
  type BillingTabKey,
  type BillingTabUiState,
} from './slices/billingUiSlice';

export type StoreState = AuthSlice &
  AppSlice &
  PatientsSlice &
  DocumentsSlice &
  TasksSlice &
  IntakeSlice &
  ToastSlice &
  UploadsSlice &
  PayorSlice &
  RogerDocSlice &
  AgencyInsuranceSlice &
  AgencyClinicSlice &
  NOASlice &
  OASISManagerSlice &
  PayrollSlice &
  FormSchemaLayoutGenerationSlice &
  FormSchemaEditorSlice &
  VibecoderSettingsSlice &
  BillingUiSlice;

export const useStore = create<StoreState>()(
  devtools(
    persist(
      (...a) => ({
        ...authSlice(...a),
        ...appSlice(...a),
        ...patientsSlice(...a),
        ...documentsSlice(...a),
        ...tasksSlice(...a),
        ...intakeSlice(...a),
        ...toastSlice(...a),
        ...uploadsSlice(...a),
        ...payorSlice(...a),
        ...rogerDocSlice(...a),
        ...agencyInsuranceSlice(...a),
        ...agencyClinicSlice(...a),
        ...noaSlice(...a),
        ...oasisManagerSlice(...a),
        ...payrollSlice(...a),
        ...formSchemaLayoutGenerationSlice(...a),
        ...formSchemaEditorSlice(...a),
        ...vibecoderSettingsSlice(...a),
        ...billingUiSlice(...a),
      }),
      {
        name: 'roger-emr-store',
        version: 2,
        migrate: (persisted: unknown, fromVersion: number) => {
          const state = persisted as {
            billingUiState?: Record<string, BillingTabUiState>;
          };
          // v2 (ROG-2913): billing tabs became server-driven — drop legacy
          // client-side columnFilters and ensure insuranceIds/clinicIds exist.
          if (fromVersion < 2 && state?.billingUiState) {
            state.billingUiState = migrateBillingUiState(
              state.billingUiState as Partial<
                Record<BillingTabKey, BillingTabUiState>
              >
            );
          }
          return state as never;
        },
        partialize: state => ({
          user: state.user,
          isAuthenticated: state.isAuthenticated,
          isSidebarCollapsed: state.isSidebarCollapsed,
          intakeManagerFilters: state.intakeManagerFilters,
          noaManagerFilters: state.noaManagerFilters,
          oasisManagerFilters: state.oasisManagerFilters,
          vibecoderModelId: state.vibecoderModelId,
          vibecoderTemperature: state.vibecoderTemperature,
          billingUiState: state.billingUiState,
          remittanceUiState: state.remittanceUiState,
        }),
      }
    ),
    {
      name: 'roger-emr-store',
    }
  )
);
